import { useState, useRef, useEffect } from "react";
import { handleCustomPrompt } from "./documentUtils";

export default function PromptBox({ documentText, contextText, auth, promptHistory, setPromptHistory }) {
  const [expanded, setExpanded] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  const handleExpand = () => {
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleHide = () => {
    setExpanded(false);
    setInput("");
    setPromptHistory([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setLoading(true);
    try {
      const { answer, error } = await handleCustomPrompt({
        prompt: input,
        contextText,
        documentText,
        auth,
      });
      setPromptHistory((prev) => [
        ...prev,
        {
          prompt: input,
          reply: error ? `Error: ${error}` : answer || "(No reply)",
        },
      ]);
      setInput(""); // Clear only the input box
    } catch (err) {
      setPromptHistory((prev) => [
        ...prev,
        {
          prompt: input,
          reply: "Error: " + err.message,
        },
      ]);
      setInput("");
    }
    setLoading(false);
  };

  // Scroll to bottom when history changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  }, [promptHistory, expanded]);

  return (
    <div
      className={`w-full mt-4 flex flex-col items-center`}
      style={{
        height: expanded ? "50vh" : "2.5rem", // 2.5rem is about one input line
        minHeight: expanded ? "320px" : "0",
        transition: "height 0.3s",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* Read-only reply textarea showing history */}
      {expanded && (
        <textarea
          ref={textareaRef}
          className="border rounded px-3 py-2 text-sm bg-gray-100 mb-2"
          style={{
            width: "100%",
            height: "100%", // Fill parent div
            minHeight: "120px",
            resize: "none",
            transition: "height 0.3s",
            boxSizing: "border-box",
          }}
          rows={8}
          value={
            promptHistory.length === 0
              ? "(reply will appear here)"
              : promptHistory
                  .map(
                    (item, idx) =>
                      `> ${item.prompt}\n${item.reply}${
                        idx < promptHistory.length - 1 ? "\n\n" : ""
                      }`
                  )
                  .join("")
          }
          readOnly
        />
      )}
      <form
        className="w-full flex items-center relative"
        onSubmit={handleSubmit}
        style={{
          height: expanded ? "auto" : "2.5rem",
          alignItems: "center",
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className={`border rounded px-3 py-2 text-sm transition-all duration-200 ${
            expanded ? "mb-2" : ""
          }`}
          placeholder="> Ask Grr a question..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={handleExpand}
          onClick={handleExpand}
          style={{
            width: "calc(100% - 2rem)",
            transition: "width 0.3s",
            height: "2rem",
          }}
          disabled={loading}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              await handleSubmit(e);
            }
          }}
        />
        {expanded && (
          <button
            type="button"
            className="text-xs text-gray-400 border-none bg-transparent p-0"
            onClick={handleHide}
            aria-label="Hide"
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              height: "1.25rem",
              width: "1.25rem",
              lineHeight: "1",
              fontSize: "1rem",
              cursor: "pointer",
              padding: 0,
            }}
            disabled={loading}
          >
            {loading ? "⏳" : "✖"}
          </button>
        )}
      </form>
    </div>
  );
}